import type {
  ChatResponse,
  ConversationSummary,
  ConversationDetail,
  ApiError,
} from './types';

// Worker API base. When PWA is hosted at myfelix.xyz (same origin as Worker), use ''.
// When PWA is on *.pages.dev and Worker is on workers.dev or myfelix.xyz, point at the absolute URL.
// Auto-detect: if we're on a pages.dev or localhost host, use the workers.dev URL.
// If we're on myfelix.xyz, use same-origin.
const BASE = (() => {
  if (typeof location === 'undefined') return '';
  const host = location.hostname;
  if (host === 'myfelix.xyz') return '';
  // Default to workers.dev for pages.dev preview deploys and local dev fallback
  return 'https://nucleus-proxy.paramountbets.workers.dev';
})();

const TOKEN_KEY = 'nucleus.sharedSecret';
const TOKEN_LAST_USED_KEY = 'nucleus.tokenLastUsed';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TOKEN_LAST_USED_KEY, Date.now().toString());
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_LAST_USED_KEY);
}

export class ApiException extends Error {
  status: number;
  body: ApiError;
  constructor(status: number, body: ApiError) {
    super(body.error || `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  if (!token) {
    throw new ApiException(401, { error: 'no_token' });
  }

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  });

  let body: any;
  try {
    body = await res.json();
  } catch {
    body = { error: 'parse_error', message: `Non-JSON response from ${path}` };
  }

  if (!res.ok) {
    throw new ApiException(res.status, body);
  }

  // Touch last-used timestamp on successful auth (for "rotate every X days" reminders later)
  localStorage.setItem(TOKEN_LAST_USED_KEY, Date.now().toString());

  return body as T;
}

export async function sendMessage(
  conversationId: string | null,
  message: string,
  model = 'claude-sonnet-4-6'
): Promise<ChatResponse> {
  return apiFetch<ChatResponse>('/api/chat', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: conversationId || undefined,
      message,
      model,
    }),
  });
}

export async function listConversations(): Promise<ConversationSummary[]> {
  const data = await apiFetch<{ conversations: ConversationSummary[] }>('/api/conversations');
  return Array.isArray(data?.conversations) ? data.conversations : [];
}

export async function getConversation(id: string): Promise<ConversationDetail> {
  return apiFetch<ConversationDetail>(`/api/conversations/${id}`);
}

export async function archiveConversation(id: string): Promise<void> {
  await apiFetch(`/api/conversations/${id}`, { method: 'DELETE' });
}

export interface ProviderBalance {
  provider: string;
  status: 'ok' | 'unavailable' | 'not_wired';
  spend_usd?: number;
  requests?: number;
  tokens_in?: number;
  tokens_out?: number;
  window?: string;
  dashboard_url?: string;
  note?: string;
}

export async function getBalances(days = 7): Promise<{ window_days: number; providers: ProviderBalance[] }> {
  return apiFetch<{ window_days: number; providers: ProviderBalance[] }>(`/api/balances?days=${days}`);
}

export async function healthCheck(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/api/health`);
    if (!res.ok) return false;
    const body = await res.json();
    return body.ok === true;
  } catch {
    return false;
  }
}

/** Test a token against the API by listing conversations. Used during Setup flow. */
export async function validateToken(token: string): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/api/conversations`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.ok;
  } catch {
    return false;
  }
}
